import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react'
import { Box, CircularProgress, Dialog, Stack } from '@mui/material'
import { useSelector } from 'react-redux'
import { useAuthContext } from '@/features/auth/context'
import useFlowValvesForPurposeCheck from '@/features/config/hooks/controller/useFlowValvesForPurposeCheck'
import useAcknowledgePipeErrors from '@/features/config/hooks/hydraulic/flowValve/useAcknowledgePipeErrors'
import { usePermission } from '@/hooks/permissions'
import useTranslation from '@/hooks/useTranslation'
import { useUnits } from '@/hooks/useUnits'
import { useConfigUI } from '../../../context'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'
import ConfirmDeleteModalUI from '@/components/common/ConfirmDeleteModalUI/ConfirmDeleteModalUI'
import FlowAlarmDialog from '../../HydraulicRule/FlowRulePanel/FlowAlarmDialog'
import FlowSectionPopover from '../../HydraulicRule/FlowRulePanel/FlowSectionPopover'
import DataGrid, { gridRowClasses } from '@/components/grid/DataGrid'
import type { GridApi } from '@/components/grid/types'
import classes from '../style.module.scss'
import { ROW_HEIGHT } from './constants'
import { AddSubPipeChip } from './chips'
import { draftTreeRow, isDraftId, rowKey } from './tree-data'
import { indexValvesByRule } from './flow-rule-state'
import { cellRowId, findPipeProblems, onlyMissingValues } from './save-rules'
import type {
  HydraulicTableProps,
  HydraulicTableRef,
  HydraulicTreeRow,
} from './types'
import useAddSubPipeOverlay from './use-add-sub-pipe-overlay'
import useErrorClearing from './use-error-clearing'
import useCellRenderers from './use-cell-renderers'
import useColumnVisibility from './use-column-visibility'
import useFlowRuleActions from './use-flow-rule-actions'
import useHydraulicColumns from './use-hydraulic-columns'
import useHydraulicDrafts from './use-hydraulic-drafts'
import useHydraulicEdits from './use-hydraulic-edits'
import useHydraulicSave from './use-hydraulic-save'
import useSeedMainline from './use-seed-mainline'
import CascadeDeleteNotice from '@/features/config/components/modal/CascadeDeleteNotice'
import useGuidedAutosave from '@/features/config/hooks/useGuidedAutosave'
import { useIsGuidedSetup } from '@/features/config/contexts/guided-edit-context'

export type { HydraulicTableRef } from './types'

const HydraulicTable = forwardRef<HydraulicTableRef, HydraulicTableProps>(
  (
    {
      interfaceId: propInterfaceId,
      rows,
      onDelete,
      bulkEditMode = false,
      onToggleBulkEdit,
      onRefetch,
      onBodyScrollEnd,
      isFetchingNextPage,
      loading,
    },
    ref,
  ) => {
    const { t } = useTranslation()
    const {
      isMetric,
      convertFromMetricV2,
      convertToMetricV2,
      convertLabelUnit,
      getImperialUnitLabel,
    } = useUnits()
    // useUnits hands back fresh closures every render, so the converting
    // columns are pinned to the measurement system itself.
    const units = useMemo(
      () => ({ convertFromMetricV2, convertToMetricV2, getImperialUnitLabel }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [isMetric],
    )
    const { isViewOnly } = usePermission()
    const isDark = useSelector(selectTheme) === mode_type.dark
    const { interfaceId: selectedInterfaceId } = useAuthContext()
    const authInterfaceId = Number(propInterfaceId ?? selectedInterfaceId)
    const { openSidebar, setSidebarView } = useConfigUI()

    const gridApiRef = useRef<GridApi<HydraulicTreeRow> | null>(null)
    const gridContainerRef = useRef<HTMLDivElement>(null)
    const rowDataRef = useRef<HydraulicTreeRow[]>([])

    const {
      drafts,
      draftsRef,
      setDrafts,
      addDraft,
      handleDiscardDraft,
      discardDraftsUnder,
      updateDraftField,
      resetDrafts,
      invalidCells,
      setInvalidCells,
    } = useHydraulicDrafts({
      bulkEditMode,
      isViewOnly,
      authInterfaceId,
      gridApiRef,
      rowDataRef,
    })

    const {
      gridData,
      setGridData,
      addPipeRow,
      editedRowsRef,
      editedCellsRef,
      editVersion,
      bumpEditVersion,
      userEditVersion,
      handleCellValueChanged,
      forgetPipeEdits,
    } = useHydraulicEdits({
      rows,
      bulkEditMode,
      authInterfaceId,
      units,
      updateDraftField,
    })

    // Drafts are spliced into the tree here rather than held in `gridData`, so
    // a refetch can rebuild the server rows underneath them without losing any.
    // A draft carries its parent's path with its own segment appended, which is
    // what nests it under that pipe instead of dropping it at the bottom.
    const rowData = useMemo(() => {
      if (drafts.length === 0) {
        return addPipeRow ? [...gridData, addPipeRow] : gridData
      }

      const pathById = new Map<number, string[]>()
      gridData.forEach((row) => pathById.set(row.id, row.path))

      const merged = [...gridData]
      // Parent drafts are staged before their children, so a nested draft
      // always finds its parent's path already recorded here.
      drafts.forEach((draft) => {
        const parentPath = draft.parentId ? pathById.get(draft.parentId) : []
        // A parent that has gone (deleted elsewhere, or scrolled out of the
        // pages fetched so far) leaves the draft visible at the root rather
        // than silently dropped.
        const path = [...(parentPath ?? []), `__DRAFT_${draft.id}__`]
        pathById.set(draft.id, path)
        merged.push(draftTreeRow(draft, path))
      })

      return addPipeRow ? [...merged, addPipeRow] : merged
    }, [gridData, addPipeRow, drafts])
    rowDataRef.current = rowData

    const getViewport = useCallback(
      () => gridApiRef.current?.getViewportEl() ?? null,
      [],
    )

    // --- Row actions ------------------------------------------------------

    const handleAddSubPipe = useCallback(
      (id: string) => {
        if (isViewOnly) return
        openSidebar()
        setSidebarView({ type: 'HYDRAULIC_VIEW', parentId: id })
      },
      [isViewOnly, openSidebar, setSidebarView],
    )

    const handleEdit = useCallback(
      (id: string, parentId: string) => {
        if (isViewOnly) return
        openSidebar()
        setSidebarView({ type: 'HYDRAULIC_VIEW', id, parentId })
      },
      [isViewOnly, openSidebar, setSidebarView],
    )

    const handleDelete = useCallback(
      (
        id: number,
        rowInterfaceId: number,
        flowRuleId?: number,
        name?: string,
      ) => {
        if (isViewOnly) return
        // SCRUM-1032: the rule travels with the request, so the confirmation
        // can say what else the pipe takes with it.
        onDelete(
          id,
          String(rowInterfaceId || authInterfaceId),
          flowRuleId ? String(flowRuleId) : undefined,
          name,
        )
      },
      [isViewOnly, onDelete, authInterfaceId],
    )

    // The flow valves on each rule, so a row can clear its own errors. There is
    // no per-pipe reset in the API — `reset-error-by-id` takes a valve and
    // clears its parent rule with it, and clearing the rule is what clears the
    // pipe's status.
    // Every valve on the interface, not the first page of 10: a rule whose
    // valves sat past it had no id to clear through, and its fault went
    // unflagged (SCRUM-1032 review). Keyed under queryKeys.flowValve, so the
    // valve list invalidations refresh it.
    const { data: allValves } = useFlowValvesForPurposeCheck(Number(authInterfaceId))
    const { valveIdsByRuleId, rulesWithValveError } = useMemo(
      () => indexValvesByRule(allValves ?? []),
      [allValves],
    )

    const { mutateAsync: acknowledgePipe } = useAcknowledgePipeErrors()

    // Clearing an alarm is a request, not a result: the rule's state is
    // recomputed continuously, so an error whose cause is still present comes
    // straight back. This watches for that and says so.
    const { clearingIds, returnedIds, clearErrors, dismissReturned } =
      useErrorClearing({ rowDataRef, onRefetch })

    const handleAcknowledge = useCallback(
      (row: HydraulicTreeRow) => {
        if (isViewOnly) return
        const valveIds = valveIdsByRuleId.get(Number(row.flowRuleId)) ?? []
        // A rule with no valves has no id to reset through. The button is
        // disabled in that case, so this is only a guard.
        if (valveIds.length === 0) return
        clearErrors(row.id, () =>
          acknowledgePipe({ interfaceId: Number(authInterfaceId), valveIds }),
        )
      },
      [
        isViewOnly,
        acknowledgePipe,
        authInterfaceId,
        valveIdsByRuleId,
        clearErrors,
      ],
    )

    const canAcknowledge = useCallback(
      (row: HydraulicTreeRow) =>
        (valveIdsByRuleId.get(Number(row.flowRuleId)) ?? []).length > 0,
      [valveIdsByRuleId],
    )

    // --- Bulk save / cancel ----------------------------------------------

    // SCRUM-1032: guided setup stays in bulk edit across saves.
    const isGuidedSetup = useIsGuidedSetup()

    const { handleSave, handleCancel, isSaving } = useHydraulicSave({
      gridApiRef,
      draftsRef,
      setDrafts,
      setInvalidCells,
      resetDrafts,
      editedRowsRef,
      editedCellsRef,
      bumpEditVersion,
      gridData,
      getViewport,
      authInterfaceId,
      onRefetch,
      onToggleBulkEdit,
      stayInBulkEdit: isGuidedSetup,
      t,
    })

    // SCRUM-1032: in the guided setup the pipe tree writes itself once the user
    // pauses, the same as the decoder list. Not on /config, where Save is the
    // contract.
    // A half-typed new pipe is not ready to be written: `handleSave` rejects a
    // draft with no description or no capacity and raises a toast for it, which
    // on a timer would fire every few seconds while the user is still filling
    // the row in. Edits to existing pipes carry no such requirement, so they
    // autosave regardless; a draft waits until it is complete.
    //
    // Edits to existing pipes are not held here: the save checks them with the
    // same rule and marks the cell, which is the feedback a user who typed -10
    // needs. It runs once per edit — the version below moves on user edits
    // only — so the check cannot turn into a toast every few seconds.
    //
    // Round 7 review (H8): the hold is now on the drafts only, inside the
    // save (`holdIncompleteDrafts`). Gating `enabled` on them meant one blank
    // new pipe also stopped every edit to an existing pipe from autosaving.
    // A draft still being typed waits; one holding a value the server will
    // refuse arms the autosave, which then says why (round 7 follow-up).
    const draftProblems = findPipeProblems(drafts)
    const draftsReadyToSave =
      draftProblems.size === 0 || !onlyMissingValues(draftProblems)
    const autosave = useCallback(
      () => handleSave({ holdIncompleteDrafts: true }),
      [handleSave],
    )

    const forgetPipe = useCallback(
      (id: number) => {
        const gone = new Set(forgetPipeEdits(id))
        setInvalidCells((previous) => {
          const next = new Set(
            [...previous].filter((key) => !gone.has(cellRowId(key))),
          )
          return next.size === previous.size ? previous : next
        })
        // New pipes staged under it go too: their parent no longer exists,
        // so they could never be written and would hold the step.
        discardDraftsUnder(gone)
      },
      [forgetPipeEdits, setInvalidCells, discardDraftsUnder],
    )

    useGuidedAutosave({
      enabled: isGuidedSetup && bulkEditMode && !isViewOnly,
      hasPendingChanges:
        Object.keys(editedRowsRef.current).length > 0 ||
        (drafts.length > 0 && draftsReadyToSave),
      // User edits only. Not `editVersion`: the save bumps that to repaint,
      // and a failed save re-armed itself through it (round 7, item 3). Not
      // `drafts.length` either: a save that creates a draft shortens the list,
      // which re-armed one more resend of whatever had just failed. Adding a
      // pipe needs no save of its own; filling it in is a user edit.
      version: userEditVersion,
      save: autosave,
      // An open editor is the user mid-value; the save would close it.
      isEditing: () => gridApiRef.current?.isEditingCell() ?? false,
      // `bumpEditVersion` fires part way through a save, which re-arms this
      // timer while the same drafts are still being written. Hold until that
      // run is done, or the batch is created twice.
      isBusy: isSaving,
    })

    // SCRUM-1032: a site with no pipes at all gets its first mainline made for
    // it — everything downstream (valves, pumps, sensors) needs one, and there
    // is only one sensible answer to "what is your first mainline called".
    // `loading` covers the first read only, so this waits for a settled empty
    // list rather than firing at the gap before the rows arrive; drafts count
    // too, or a staged mainline would be joined by a seeded one.
    useSeedMainline({
      enabled:
        isGuidedSetup &&
        !isViewOnly &&
        !loading &&
        rows.length === 0 &&
        drafts.length === 0,
      interfaceId: authInterfaceId,
      onRefetch,
    })

    // --- Flow rule --------------------------------------------------------

    const {
      sectionPopover,
      setSectionPopover,
      creatingRuleFor,
      deletingRule,
      deletingRuleChildren,
      loadingRuleChildren,
      setDeletingRule,
      deleteRunning,
      alarmDialogPipe,
      setAlarmDialogPipe,
      ruleActiveMap,
      handleOpenAlarm,
      handleOpenSection,
      handleRequestDeleteRule,
      handleConfirmDeleteRule,
      handleCreateRule,
    } = useFlowRuleActions({
      authInterfaceId,
      isViewOnly,
      setGridData,
      onRefetch,
      t,
    })

    // --- Columns ----------------------------------------------------------

    const renderers = useCellRenderers({
      isDark,
      isViewOnly,
      bulkEditMode,
      t,
      convertFromMetricV2,
      creatingRuleFor,
      ruleActiveMap,
      sectionPopover,
      handleCreateRule,
      handleRequestDeleteRule,
      handleOpenAlarm,
      handleOpenSection,
      rulesWithValveError,
      handleAcknowledge,
      canAcknowledge,
      clearingIds,
      returnedIds,
      dismissReturned,
      handleAddSubPipe,
      handleEdit,
      handleDelete,
      handleDiscardDraft,
      addDraft,
    })

    const { availableColumns, columns, descriptionColumn } = useHydraulicColumns(
      {
        bulkEditMode,
        isViewOnly,
        isDark,
        t,
        convertLabelUnit,
        units,
        editedCellsRef,
        editVersion,
        guidedSetup: isGuidedSetup,
        invalidCells,
        renderers,
      },
    )

    useColumnVisibility({
      availableColumns,
      columns,
      bulkEditMode,
      gridApiRef,
    })

    // --- "+ Add Sub Pipe" overlay ----------------------------------------

    const { addSubSpot, setAddSubSpot } = useAddSubPipeOverlay({
      hostRef: gridContainerRef,
      rowDataRef,
      bulkEditMode,
      isViewOnly,
    })

    // --- Public ref ------------------------------------------------------

    useImperativeHandle(
      ref,
      () => ({
        handleSave: () => handleSave(),
        handleCancel,
        forgetPipe,
        hasRoomForMoreRows: () => {
          const viewport = getViewport()
          if (!viewport) return false
          return viewport.scrollHeight <= viewport.clientHeight
        },
      }),
      [handleSave, handleCancel, forgetPipe, getViewport],
    )

    const getRowId = useCallback((row: HydraulicTreeRow) => rowKey(row), [])
    const getPath = useCallback((row: HydraulicTreeRow) => row.path, [])

    const rowClass = useCallback(
      ({ row }: { row: HydraulicTreeRow }) =>
        isDraftId(row.id) ? gridRowClasses.new : undefined,
      [],
    )

    // Memoised: the grid rebuilds its whole row model whenever this object
    // changes, so an inline literal would re-flatten the tree on every render.
    const treeData = useMemo(
      () => ({
        getPath,
        groupColumn: descriptionColumn,
        defaultExpanded: true,
        showGuides: true,
      }),
      [getPath, descriptionColumn],
    )

    return (
      <Stack
        flexGrow="1"
        sx={{ height: '100%', minWidth: 0, width: '100%', overflow: 'hidden' }}
        className={classes.content}
      >
        <Box
          ref={gridContainerRef}
          sx={{
            flex: 1,
            width: '100%',
            minWidth: 0,
            height: '100%',
            overflow: 'hidden',
            // Positioning context for the "+ Add Sub Pipe" overlay below.
            position: 'relative',
          }}
        >
          <DataGrid<HydraulicTreeRow>
            columns={columns}
            rows={rowData}
            loading={loading}
            getRowId={getRowId}
            apiRef={gridApiRef}
            rowHeight={ROW_HEIGHT}
            editMode={bulkEditMode ? 'singleClick' : 'none'}
            treeData={treeData}
            rowClass={rowClass}
            onCellValueChanged={handleCellValueChanged}
            onReachEnd={onBodyScrollEnd}
            emptyMessage={t('noData')}
          />

          {addSubSpot && (
            <Box
              sx={{
                position: 'absolute',
                top: `${addSubSpot.top}px`,
                left: `${addSubSpot.left}px`,
                transform: 'translate(-50%, -50%)',
                zIndex: 6,
              }}
            >
              <AddSubPipeChip
                label={`+ ${t('actionAddSubPipe')}`}
                isDark={isDark}
                onStart={() => {
                  const parent = rowDataRef.current.find(
                    (row) => row.id === addSubSpot.pipeId,
                  )
                  if (parent) {
                    addDraft({
                      id: parent.id,
                      level: Number(parent.level) || 1,
                      interfaceId: (parent as any).interfaceId,
                    })
                  }
                  setAddSubSpot(null)
                }}
              />
            </Box>
          )}
        </Box>

        {sectionPopover && (
          <FlowSectionPopover
            open
            anchorPosition={sectionPopover.anchorPosition}
            section={sectionPopover.section}
            interfaceId={sectionPopover.interfaceId}
            pipeId={sectionPopover.pipeId}
            pipeName={sectionPopover.name}
            flowRuleId={sectionPopover.flowRuleId}
            pipeCapacity={sectionPopover.capacity}
            onClose={() => setSectionPopover(null)}
            onRefetchPipes={onRefetch}
          />
        )}

        {alarmDialogPipe && (
          <FlowAlarmDialog
            open
            anchorPosition={alarmDialogPipe.anchorPosition}
            interfaceId={alarmDialogPipe.interfaceId}
            pipeId={alarmDialogPipe.pipeId}
            pipeName={alarmDialogPipe.name}
            pipeCapacity={alarmDialogPipe.capacity}
            flowRuleId={alarmDialogPipe.flowRuleId}
            isViewOnly={isViewOnly}
            onClose={() => setAlarmDialogPipe(null)}
            onSaved={() => onRefetch?.()}
          />
        )}

        <Dialog
          open={Boolean(deletingRule)}
          onClose={() => !deleteRunning && setDeletingRule(null)}
        >
          <ConfirmDeleteModalUI
            title={t('DeleteFlowRule')}
            name={deletingRule?.name}
            text={t('flowRuleDeleteConfirmation')}
            isLoading={deleteRunning || loadingRuleChildren}
            onClose={() => setDeletingRule(null)}
            onConfirm={handleConfirmDeleteRule}
          >
            <CascadeDeleteNotice
              children={deletingRuleChildren}
              isLoading={loadingRuleChildren}
            />
          </ConfirmDeleteModalUI>
        </Dialog>

        {isFetchingNextPage && (
          <Stack alignItems="center" py={2}>
            <CircularProgress size={24} />
          </Stack>
        )}
      </Stack>
    )
  },
)

HydraulicTable.displayName = 'HydraulicTable'

export default HydraulicTable
